/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// Gritex global WhatsApp fallback.
// Creates the button directly in the document if the section-group version
// is not present, and normalises an existing button if it is present.
function initGritexWhatsAppButton() {
  let button = document.querySelector('.gritex-whatsapp');

  if (!button) {
    button = document.createElement('a');
    button.className = 'gritex-whatsapp';
    button.href = 'https://wa.me/447590609128?text=Hi%20Gritex%2C%20I%27m%20looking%20for%20some%20advice%20about...';
    button.target = '_blank';
    button.rel = 'noopener';
    button.setAttribute('aria-label', 'Message Gritex on WhatsApp');
    button.innerHTML = `
      <span class="gritex-whatsapp__icon" aria-hidden="true">
        <svg viewBox="0 0 32 32" role="img" focusable="false">
          <path fill="currentColor" d="M16.02 3.2c-7.06 0-12.8 5.7-12.8 12.72 0 2.24.59 4.43 1.71 6.35L3.1 28.94l6.84-1.79a12.84 12.84 0 0 0 6.07 1.54h.01c7.05 0 12.79-5.71 12.79-12.73 0-3.4-1.33-6.6-3.75-9a12.77 12.77 0 0 0-9.04-3.76Zm0 23.34h-.01a10.67 10.67 0 0 1-5.44-1.49l-.39-.23-4.06 1.06 1.08-3.95-.25-.4a10.54 10.54 0 0 1-1.62-5.61c0-5.84 4.79-10.59 10.69-10.59 2.85 0 5.54 1.11 7.56 3.11a10.48 10.48 0 0 1 3.13 7.51c0 5.84-4.8 10.59-10.69 10.59Zm5.86-7.93c-.32-.16-1.9-.93-2.2-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1 1.25-.18.21-.37.24-.69.08-.32-.16-1.35-.49-2.57-1.56-.95-.84-1.59-1.88-1.78-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.7-.98-2.33-.26-.62-.52-.53-.71-.54h-.61c-.21 0-.56.08-.85.4-.29.32-1.11 1.08-1.11 2.64 0 1.55 1.14 3.05 1.3 3.26.16.21 2.24 3.4 5.43 4.77.76.32 1.35.51 1.81.65.76.24 1.45.21 2 .13.61-.09 1.9-.77 2.17-1.51.27-.74.27-1.38.19-1.51-.08-.13-.29-.21-.61-.37Z"/>
        </svg>
      </span>
      <span class="gritex-whatsapp__label">Message Gritex on WhatsApp</span>`;
  }

  if (button.parentElement !== document.body) document.body.appendChild(button);

  if (!document.getElementById('gritex-whatsapp-global-style')) {
    const style = document.createElement('style');
    style.id = 'gritex-whatsapp-global-style';
    style.textContent = `
      .gritex-whatsapp {
        position: fixed !important;
        right: 18px !important;
        bottom: 18px !important;
        z-index: 2147483000 !important;
        display: inline-flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        align-items: center !important;
        gap: 7px !important;
        min-height: 48px !important;
        padding: 10px 16px 10px 11px !important;
        border: 0 !important;
        border-radius: 999px !important;
        background: #25d366 !important;
        color: #fff !important;
        text-decoration: none !important;
        font-size: 13px !important;
        font-weight: 700 !important;
        line-height: 1.15 !important;
        box-shadow: 0 6px 18px rgba(0,0,0,.24) !important;
      }
      .gritex-whatsapp__icon { display:inline-flex !important; width:25px !important; height:25px !important; flex:0 0 25px !important; }
      .gritex-whatsapp__icon svg { display:block !important; width:100% !important; height:100% !important; }
      @media (max-width: 749px) {
        .gritex-whatsapp {
          right: 14px !important;
          bottom: 14px !important;
          width: 52px !important;
          height: 52px !important;
          min-width: 52px !important;
          min-height: 52px !important;
          padding: 0 !important;
          justify-content: center !important;
          border-radius: 50% !important;
        }
        .gritex-whatsapp__label { display:none !important; }
        .gritex-whatsapp__icon { width:29px !important; height:29px !important; flex:0 0 29px !important; }
      }
    `;
    document.head.appendChild(style);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGritexWhatsAppButton, { once: true });
} else {
  initGritexWhatsAppButton();
}
